import { isDefinedString, reduce } from "et3-type-guards";
import { isNumber, memoize } from "lodash";
import { computed, observable, runInAction } from "mobx";
import queryString from "query-string";

import getUserStore from "..";
import { apiCallGetUserMessages } from "../../../api/user-api/api-call-get-user-messages";
import {
  ALL_MESSAGES_LOAD_MORE,
  DEFAULT_TIME,
  MAX_ALL_MESSAGES_LIMIT,
  MAX_TYPED_MESSAGES_LIMIT,
  TYPED_MESSAGES_LOAD_MORE
} from "../../../constants/input-entries-constants";
import {
  getUniqueConversationData,
  parseConversationData
} from "../../../helpers/et3-conversation-parse";
import { Gender, TSeachedUser, TUserMessagesObject } from "../../../Types";
import getCoreNavigationStore from "../../core/core-navigation-store";
import getCorePopUpStore from "../../core/core-popup-store";
import getCoreUIStore from "../../core/core-ui-store";
import i18n from "../../../constants/i18n";
import { checkIfHasBadWordsAndSocialMedia } from "../../../helpers/et3-text-checker/social-media-and-bad-words";
import { TEt3PickerSingleValue } from "../../../TypesNEW";

class UserMessagesStore {
  userMessagesData = observable.box<TUserMessagesObject[]>([]);
  userMessagesType = observable.box<TEt3PickerSingleValue>({
    label: "all",
    value: "all"
  });
  searchedUser = observable.box<TSeachedUser>({
    userID: -1,
    userGender: ""
  });
  searchedMessageType = observable.box<string>("all");
  hasMoreMessages = observable.box<boolean>(false);
  coloredReceipientMessage = observable.map<number, boolean>();
  messageEra = observable.box<TEt3PickerSingleValue>({
    label: "POST_2025",
    value: "POST_2025"
  });
  setMessageEra = (limit: TEt3PickerSingleValue) => {
    runInAction(() => {
      this.messageEra.set(limit);
    });
  };

  checkURLSearch = async (search: string) => {
    if (isDefinedString(search)) {
      const searchID = queryString.parse(search).id;
      if (isDefinedString(searchID) && searchID !== "0") {
        const searchIDNumber = parseInt(searchID, 10);
        getUserStore().setUserIDInput(searchIDNumber);
        await this.getUserMessages({
          isLoadMore: false
        });
      }
    }
  };

  setUserMessagesData = (messages: TUserMessagesObject[]) => {
    runInAction(() => {
      this.userMessagesData.set(messages);
    });
  };

  setUserHasMoreMessages = (status: boolean) => {
    runInAction(() => {
      this.hasMoreMessages.set(status);
    });
  };

  setUserMessagesType = (type: TEt3PickerSingleValue) => {
    runInAction(() => {
      this.userMessagesType.set(type);
    });
  };

  setLastMessagesType = (messageType: string) => {
    runInAction(() => {
      this.searchedMessageType.set(messageType);
    });
  };

  searchUserMessages = async () => {
    await this.getUserMessages({ isLoadMore: false });
    getCoreNavigationStore().setURLSearchID(getUserStore().userIdInput.get());
  };

  getUserMessages = async ({ isLoadMore }: { isLoadMore: boolean }) => {
    const userId = getUserStore().userIdInput.get();
    if (!isNumber(userId) || userId === -1) {
      getCorePopUpStore().showAlertMessage("WRONG_USER_ID");
      return;
    }
    getCoreUIStore().setIsLoading("USER_MESSAGES", true);
    let oldMessages: TUserMessagesObject[] = [];

    const messageType = this.userMessagesType.get().value;
    const allMessagesType = messageType === "all";
    let limit = allMessagesType
      ? MAX_ALL_MESSAGES_LIMIT
      : MAX_TYPED_MESSAGES_LIMIT;
    const allMessageType = messageType === "all";
    let startMessageSentOn = DEFAULT_TIME;
    let startMessageId = 0;
    if (
      isLoadMore &&
      this.searchedMessageType.get() === this.userMessagesType.get().value
    ) {
      oldMessages = this.userMessagesData.get();
      const oldMessagesLength = oldMessages.length;
      const firstMessageIndex = oldMessagesLength - 1;
      startMessageSentOn = reduce(
        () => oldMessages[firstMessageIndex].sentOn,
        DEFAULT_TIME
      );
      startMessageId = reduce(
        () => oldMessages[firstMessageIndex].messageId,
        0
      );
      limit = allMessageType
        ? ALL_MESSAGES_LOAD_MORE
        : TYPED_MESSAGES_LOAD_MORE;
    }
    const { data, code, status } = await apiCallGetUserMessages({
      userId,
      limit,
      messageType,
      startMessageId,
      startMessageSentOn,
      messageEra: this.messageEra.get().value
    });

    const parsedData = parseConversationData(data.messages);

    const newData = oldMessages.concat(parsedData);
    const uniqueData = getUniqueConversationData({
      data: newData,
      uniqueBy: "messageId"
    });
    this.setUserMessagesData(uniqueData);
    this.setUserHasMoreMessages(data.userHasMoreMessages);
    this.searchedUser.set(data.searchedUser);
    this.setLastMessagesType(messageType);
    getCoreUIStore().setIsLoading("USER_MESSAGES", false);
    if (status === "ERROR") {
      getCorePopUpStore().showAlertMessage(code);
    }
  };

  shouldShowLoadMoreButton = computed(() => {
    const messagesType = this.userMessagesType.get().value;
    const searchedMessagesType = this.searchedMessageType.get();
    const sameMessagesField = messagesType === searchedMessagesType;
    const hasMoreMessagesInDatabase = this.hasMoreMessages.get();
    const shouldShow = sameMessagesField && hasMoreMessagesInDatabase;
    return shouldShow;
  });

  getBadWordsCounterColor = (text: string) => {
    switch (text) {
      case i18n.get("SOCIAL_MEDIA_ACCOUNT"):
        return "warning";
      default:
        return "error";
    }
  };

  getBadWordsAndSocialMediaCount(messagesData: TUserMessagesObject[]) {
    let badWords = 0;
    let socialMedia = 0;
    const messagesCount = messagesData.map((messageData) => {
      const userMessages = messageData.messageObject.message;
      const { hasBadWords, hasSocialMedia } =
        checkIfHasBadWordsAndSocialMedia(userMessages);

      if (hasBadWords) {
        badWords = badWords + 1;
        return messageData;
      }
      if (hasSocialMedia) {
        socialMedia = socialMedia + 1;
        return messageData;
      }

      if (hasSocialMedia) {
        socialMedia = socialMedia + 1;
        return messageData;
      }
      return messageData;
    }).length;
    return [
      {
        text: i18n.get("SOCIAL_MEDIA_ACCOUNT"),
        count: socialMedia,
        messagesCount: messagesCount
      },
      {
        text: i18n.get("BAD_WORDS"),
        count: badWords,
        messagesCount: messagesCount
      }
    ];
  }

  getMessagesData = (
    messagesData: TUserMessagesObject[],
    searchedUser: TSeachedUser
  ) => {
    return messagesData.map((message) => {
      const isMale = searchedUser.userGender === Gender.Male;
      const isSenderID = searchedUser.userID === message.senderId;
      const senderIdGender = isSenderID
        ? searchedUser.userGender
        : isMale
          ? Gender.Female
          : Gender.Male;
      return {
        ...message,
        gender: senderIdGender,
        message: message.messageObject.message,
        messageType: message.messageObject.messageType
      };
    });
  };

  setRecipientColored = (value: number, state: boolean) => {
    runInAction(() => {
      this.coloredReceipientMessage.set(value, state);
    });
  };
}

const getUserMessagesStore = memoize(
  () => new UserMessagesStore(),
  () => 1
);
export default getUserMessagesStore;
